import type { ChannelRow } from '../api/channels';
import type { CategoryRow } from '../api/categoriesApi';

/** Kolejność kategorii jak w bieżącej tablicy (pierwsze wystąpienie) — do ponownego sortu po edycji. */
export function categoryOrderMapFromRowSequence(rows: ChannelRow[]): Record<string, number> {
  const keys: string[] = [];
  for (const c of rows) {
    const cat = c.category || 'UNCATEGORIZED';
    if (!keys.includes(cat)) keys.push(cat);
  }
  const m: Record<string, number> = {};
  keys.forEach((k, i) => {
    m[k] = i;
  });
  return m;
}

export function buildCategoryOrderMap(categories: CategoryRow[]): Record<string, number> {
  const m: Record<string, number> = {};
  categories.forEach((cat, idx) => {
    m[cat.name] = cat.sort_order !== undefined ? cat.sort_order : idx;
  });
  return m;
}

/** Kolejność nazw kategorii jak pierwsze wystąpienie w posortowanej tablicy kanałów (zgodnie z siatką). */
export function categoryNamesInPlaylistRowOrder(
  rows: ChannelRow[],
  displayKey: (cat: string | null | undefined) => string = (c) =>
    c == null || c === '' ? 'UNCATEGORIZED' : String(c),
): string[] {
  const keys: string[] = [];
  for (const row of rows) {
    const k = displayKey(row.category);
    if (!keys.includes(k)) keys.push(k);
  }
  return keys;
}

/**
 * Opcje listy kategorii: najpierw kolejność jak w siatce, potem puste grupy z DB (tylko nazwa, bez kanałów) wg sort_order.
 */
export function categoryPickerOptionsOrdered(
  sortedChannelRows: ChannelRow[],
  dbCategories: CategoryRow[],
  displayKey: (cat: string | null | undefined) => string,
): string[] {
  const fromGrid = categoryNamesInPlaylistRowOrder(sortedChannelRows, displayKey);
  /** Klucze jak w pickerze — porównuj z DB po displayKey, inaczej np. "Sport" ≠ "SPORT" i duplikujemy wpis. */
  const inGrid = new Set(fromGrid);
  const orderMap = buildCategoryOrderMap(dbCategories);
  const skip = new Set(['Uncategorized', 'UNCATEGORIZED']);
  const emptyOnly = dbCategories
    .map((c) => c.name)
    .filter((name) => !inGrid.has(displayKey(name)) && !skip.has(name));
  emptyOnly.sort((a, b) => (orderMap[a] ?? 999999) - (orderMap[b] ?? 999999));
  return [...fromGrid, ...emptyOnly];
}

export function sortRowsByCategoryOrder(
  channels: ChannelRow[],
  categoryOrderMap: Record<string, number>,
): ChannelRow[] {
  const copy = channels.map((c) => ({
    ...c,
    sort_order: c.sort_order !== undefined ? c.sort_order : c.channel_number,
  }));
  copy.sort((a, b) => {
    const catA = a.category || 'UNCATEGORIZED';
    const catB = b.category || 'UNCATEGORIZED';
    const orderA = categoryOrderMap[catA] ?? 999999;
    const orderB = categoryOrderMap[catB] ?? 999999;
    if (orderA !== orderB) return orderA - orderB;
    const so = (a.sort_order || 0) - (b.sort_order || 0);
    if (so !== 0) return so;
    const num = (a.channel_number ?? 0) - (b.channel_number ?? 0);
    if (num !== 0) return num;
    return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
  });
  return copy;
}
